import { inject, Injectable, isDevMode } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngxs/store';
import {
  CacheBucket,
  CacheVersionsMap,
  KNOWN_BUCKETS
} from '../../interfaces/cache-versions/cache-versions.interface';
import { CacheVersionsApiService } from './cache-versions-api.service';
import { CacheVersionsStorageService } from './cache-versions-storage.service';
import {
  BucketInvalidationAction,
  getBucketInvalidations
} from './cache-bucket-invalidators';
import { HappyStoreInfoCacheService } from '../happy/happy-store-info-cache.service';
import { HappyTenantContextService } from '../happy/happy-tenant-context.service';

const KNOWN_BUCKET_SET: ReadonlySet<CacheBucket> = new Set(KNOWN_BUCKETS);

/**
 * Reconciles the locally-persisted cache versions map with the latest
 * versions reported by the server, invalidating only the affected client
 * caches.
 *
 * Triggered from:
 *  - app boot (`provideAppInitializer`)
 *  - app foreground (`visibilitychange`) and a periodic 60s timer in
 *    `AppComponent`
 *  - entry into catalog-heavy state actions (e.g. `GetRestaurantProductsAction`)
 *
 * Safe to call from manual refresh / pull-to-refresh hooks: in-flight calls
 * are de-duplicated and any failure is a no-op for existing caches.
 */
@Injectable({ providedIn: 'root' })
export class CacheVersionsReconcileService {
  private readonly api: CacheVersionsApiService = inject(
    CacheVersionsApiService
  );
  private readonly storage: CacheVersionsStorageService = inject(
    CacheVersionsStorageService
  );
  private readonly store: Store = inject(Store);
  private readonly storeInfoCache: HappyStoreInfoCacheService = inject(
    HappyStoreInfoCacheService
  );
  private readonly tenantContext: HappyTenantContextService = inject(
    HappyTenantContextService
  );

  private inFlight: Promise<void> | null = null;

  reconcile(): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.runReconcile().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async runReconcile(): Promise<void> {
    let next: CacheVersionsMap | null;
    try {
      next = await firstValueFrom(this.api.getCacheVersions());
    } catch {
      return;
    }
    if (!next) {
      return;
    }

    const prev = this.storage.load();
    const changed = this.diff(prev, next);
    if (changed.size === 0) {
      return;
    }

    this.invalidate(changed);
    this.storage.save(this.merge(prev, next));

    if (isDevMode()) {
      // eslint-disable-next-line no-console
      console.debug('[cache-versions] changed buckets:', [...changed]);
    }
  }

  private diff(
    prev: CacheVersionsMap | null,
    next: CacheVersionsMap
  ): Set<CacheBucket> {
    const changed = new Set<CacheBucket>();
    for (const bucket of KNOWN_BUCKETS) {
      const nextValue = next[bucket];
      if (nextValue === undefined) {
        continue;
      }
      const prevValue = prev ? prev[bucket] : undefined;
      if (prevValue === undefined || prevValue !== nextValue) {
        changed.add(bucket);
      }
    }
    return changed;
  }

  private invalidate(changed: Set<CacheBucket>): void {
    const seen = new Set<string>();
    const queue: BucketInvalidationAction[] = [];
    for (const bucket of changed) {
      const actions = getBucketInvalidations(bucket, {
        store: this.store,
        storeInfoCache: this.storeInfoCache,
        tenantContext: this.tenantContext
      });
      for (const action of actions) {
        if (seen.has(action.id)) {
          continue;
        }
        seen.add(action.id);
        queue.push(action);
      }
    }

    for (const action of queue) {
      try {
        action.run();
      } catch (error) {
        if (isDevMode()) {
          // eslint-disable-next-line no-console
          console.warn(
            `[cache-versions] invalidator "${action.id}" failed:`,
            error
          );
        }
      }
    }
  }

  private merge(
    prev: CacheVersionsMap | null,
    next: CacheVersionsMap
  ): CacheVersionsMap {
    const merged: CacheVersionsMap = { ...(prev ?? {}) };
    for (const bucket of KNOWN_BUCKETS) {
      const value = next[bucket];
      if (value !== undefined) {
        merged[bucket] = value;
      }
    }
    for (const key of Object.keys(next)) {
      if (!KNOWN_BUCKET_SET.has(key as CacheBucket)) {
        // Ignore unknown buckets entirely - don't persist them either.
        continue;
      }
    }
    return merged;
  }
}

/**
 * Public reusable entry point for the cache-version reconcile routine.
 * Exposed as a convenience wrapper so call sites can trigger reconciliation
 * without depending directly on the service class shape.
 */
export function reconcileTenantCacheVersions(
  service: CacheVersionsReconcileService
): Promise<void> {
  return service.reconcile();
}
